import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isNil } from "lodash";

import locationService from "@plannel/services/data/location-service";
import siteService from "@plannel/services/data/site-master-service";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { Autocomplete, TextField, Backdrop, CircularProgress } from "@mui/material";
import { reloadLocationFilter } from "@plannel/components/filter/LocationFilter";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { locationType } = CodeUtil;
const { snackBarMessageColor } = ColorUtil;
const LocationMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [locationDropdownList, setlocationDropdownList] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.page : 1);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [tPages, setTotalPages] = useState(0);

  const [sortParams, setSortParams] = useState({});
  const searchCode = useRef(reduxViewState?.searchCode ?? "");
  const [sites, setSites] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? null);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationValueFormatter = params => {
    const { value } = params;

    return locationType?.[value] ? GridUtils.gridValueL10N(locationType[value]?.name) : value;
  }

  const siteCdValueFormatter = (site) => {
    const { value } = site;
    if (!value || typeof value !== "object") {
      return "";
    }

    return value?.siteCd ||"";
  };

  const siteNameValueFormatter = (site) => {
    const { value } = site;
    if (!value || typeof value !== "object") {
      return "";
    }

    return value.name || "";
  };

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];

    return nullSafeComparator(val1, val2, isDescending);
  }

  // 동결 구간은 버킷 개수이므로 0 이상의 정수만 허용한다. 미입력은 null(미설정)로 저장.
  const frozenRangeValueSetter = params => {
    const { newValue } = params;

    if (isNil(newValue) || newValue === "") {
      params.data.frozenRange = null;

      return true;
    }

    const frozenRange = Number(newValue);
    if (Number.isNaN(frozenRange) || !Number.isInteger(frozenRange) || frozenRange < 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntGreaterThanValue", { N: 0 }) });

      return false;
    }

    params.data.frozenRange = frozenRange;

    return true;
  }

  const checkActiveFlgChanges = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

    return allStateData.some((row) => {
      const originalData = updatedOrigin[row.rowIndex];
      if (originalData) {
        return originalData.activeFlg !== row.activeFlg && row.activeFlg === false;
      }
      return false;
    });
  };

  const handleSaveClick = () => {
    gridRef.current?.api?.stopEditing();

    const hasActiveFlgChanges = checkActiveFlgChanges();

    if (hasActiveFlgChanges) {
      setDialogInfo({ open: true, title: "save", content: `${t("MSG_ActiveStatusChangedAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.location, 'deactivate')}` });
    } else {
      setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
    }
  };

  const columnDefs = [
    {
      headerName: "locCd",
      field: "locCd",
      width: 160,
      cellClass: 'stringType',
      filterType: 'string'
    },
    { headerName: "locName", field: "name", width: 140, filterType: 'string' },
    {
      headerName: "locType", field: "locType", width: 150,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: locationValueFormatter,
      cellEditorParams: {
        valueFormatter: locationValueFormatter,
        values: Object.keys(locationType)
      },
      filterType: 'string',
      filterOption: Object.keys(locationType).map(key => key.toLowerCase())
    },
    {
      headerName: "siteCd", field: "site", colId: "siteCd",
      type: ['nonEditableColumn'],
      valueFormatter: siteCdValueFormatter,
      cellClass: 'stringType',
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "siteCd", isDescending),
      width: 200,
      filterType: 'string'
    },
    {
      headerName: "siteName", field: "site", colId: "siteName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: siteNameValueFormatter,
      cellEditorParams: {
        values: sites
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 200,
      filterType: 'string'
    },
    {
      headerName: "frozenRangeSim", field: "frozenRange",
      type: ["numberColumn", "rightAligned"],
      valueSetter: frozenRangeValueSetter,
      width: 130,
      filterType: 'number',
      filterName: 'frozenRange'
    },
    { headerName: "descTxt", field: "descTxt", filterType: 'string' },
    { headerName: "inhouseFlg", field: "inhouseFlg", type: ["booleanColumn"], width: 100, filterType: 'boolean' },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 80, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (searchCode.current) {
      searchFilters.locationCd = searchCode.current;
    }

    if (locationSearch) {
      searchFilters.loc = locationSearch.locCd;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveLocationsLookUp = () => {
    locationService.getLookup({ isLocationMaster: true})
      .then(response => {
        const { results } = response?.data;

        if (!isEmpty(results)) {
          setlocationDropdownList(results);
        } else {
          setlocationDropdownList([]);
        }
      })
      .catch(e => {
        debug(e);
      })
  }


  const retrieveLocations = () => {
    const params = getRequestParams();
    locationService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setLocations(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { advancedFilters, locationSearch, searchCode: searchCode.current, currentPage, pageSize });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const retrieveSites = () => {
    siteService.getAllForCSV({})
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          setSites([{ name: t("none"), siteCd: "" }, ...results]);
        }
      })
      .catch(e => {
        debug(e);
      })
  }

  const onGridReady = params => {
    retrieveSites();
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const removeLocation = () => {
    const locationIds = [];
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        locationIds.push(data.id)
      }
    );

    setProgressOn(true);
    if (locationIds.length > 0) {
      locationService.remove(locationIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveLocations();
        })
        .finally(() => {
          setProgressOn(false);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveLocation = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

      const dataWithFlg = allStateData.map((row) => {
        const originalData = updatedOrigin[row.rowIndex];
        let activeFlgChanged = false;
        if (originalData) {
          activeFlgChanged = originalData?.activeFlg !== row?.activeFlg;
        }

        return {
          ...row,
          activeFlgChanged,
        };
      });

      setProgressOn(true);
      locationService.upsert(dataWithFlg)
        .then(() => {
          reloadLocationFilter(reduxDispatch);
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .then(() => {
          retrieveLocations();
        })
        .finally(() => {
          setProgressOn(false);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveLocation()
    } else if (action === "DELETE") {
      removeLocation()
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveLocations();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveLocations();
    }

    return () => {
      setLocations([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (!isEmpty(sites)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites]);

  useEffect(() => {
    retrieveLocationsLookUp();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setLocations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const addRow = () => {
    const newRow = {
      id: null,
      locCd: null,
      name: null,
      site: null,
      locType: null,
      frozenRange: null,
      descTxt: null,
      verNum: 0,
      activeFlg: true
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  return (
    <>
      <FilterContainer>
        <TextFieldSearch sx={{ width: 250 }} label={"locCd"} searchCode={searchCode} retrieve={initPageToRetrieve} />
        <Autocomplete
          options={locationDropdownList}
          getOptionLabel={option => option.name}
          value={locationSearch}
          onChange={(_, newValue) => setLocationSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ minWidth: 240, ml: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("location")} />
          )}
        />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={locationService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => {
            gridRef.current?.api?.stopEditing();
            setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.location, 'delete')}` })
          }} />
          <SaveButton onClick={handleSaveClick} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="location-master-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={locations}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(LocationMaster);
